import type { IMappingItem } from '@deity-io/di-data-mappings';
import type { DictMapping, StructureEditor } from '@deity-io/di-data-structure-editor';
import { flattenModelTree } from 'components/MappingEditor/helpers/mappingHelpers';
import type { ICollectionType, ISchema } from 'contracts/interfaces';
import _ from 'lodash';

export const countSchemaFields = schema => {
  let summary = {
    fields: 0,
    requiredFields: 0,
    processors: 0
  };
  const count = field => {
    Object.keys(field)
      .filter(k => k !== '_meta')
      .forEach(k => {
        if (_.has(field[k], '_meta')) {
          summary = {
            fields: summary.fields + 1,
            // eslint-disable-next-line no-underscore-dangle
            requiredFields: field[k]._meta?.required !== false ? summary.requiredFields + 1 : summary.requiredFields,
            // eslint-disable-next-line no-underscore-dangle
            processors: field[k]._meta?.processors
              ? // eslint-disable-next-line no-underscore-dangle
                summary.processors + (field[k]._meta?.processors?.length || 0)
              : summary.processors
          };
          count(field[k]);
        } else {
          summary = {
            fields: summary.fields + 1,
            requiredFields: field[k]?.required !== false ? summary.requiredFields + 1 : summary.requiredFields,
            processors: field[k]?.processors
              ? summary.processors + (field[k]?.processors?.length || 0)
              : summary.processors
          };
        }
      });
  };
  if (schema) {
    count(schema);
  }

  return summary;
};

export const countMappedRequiredFields = (mappingSchema, targetSchema) => {
  const requiredFieldsPath: string[] = [];
  const traverse = (field, path: string[]) => {
    Object.keys(field)
      .filter(k => k !== '_meta')
      .forEach(k => {
        let p: string[] = [];
        if (_.has(field[k], '_meta')) {
          // eslint-disable-next-line no-underscore-dangle
          p = [...path, field[k]._meta.target];
          // eslint-disable-next-line no-underscore-dangle
          const mappedField = _.get(targetSchema, p);
          if (mappedField?._meta?.required !== false) {
            requiredFieldsPath.push(p.join('.'));
          }
          traverse(field[k], p);
        } else {
          p = [...path, field[k].target];
          const mappedField = _.get(targetSchema, p);
          if (mappedField?.required !== false) {
            requiredFieldsPath.push(p.join('.'));
          }
        }
      });
  };

  if (mappingSchema) {
    traverse(mappingSchema, []);
  }

  return requiredFieldsPath.length;
};

export const countMappingErrors = (editor: StructureEditor, sourceSchema, targetSchema) => {
  const errors = {
    processorError: 0,
    fieldError: 0
  };
  if (editor && sourceSchema && targetSchema) {
    const flattenSourceSchema = {};
    const flattenTargetSchema = {};
    flattenModelTree(flattenSourceSchema, sourceSchema as ISchema, []);
    flattenModelTree(flattenTargetSchema, targetSchema as ISchema, []);

    Object.keys(flattenSourceSchema).forEach(path => {
      errors.processorError += fieldErrorCounter(path.split('.'), editor, true).processorError;
      errors.fieldError += fieldErrorCounter(path.split('.'), editor, true).fieldError;
    });

    Object.keys(flattenTargetSchema).forEach(path => {
      errors.processorError += fieldErrorCounter(path.split('.'), editor, false).processorError;
      errors.fieldError += fieldErrorCounter(path.split('.'), editor, false).fieldError;
    });
  }

  return errors;
};

export const fieldErrorCounter = (fieldPath: string[], editor: StructureEditor, isSource: boolean) => {
  const errors = {
    processorError: 0,
    fieldError: 0
  };

  let allMappings: Array<IMappingItem> = [];
  if (isSource) {
    const mappingField = editor.fromSourceIndex(fieldPath);
    if (mappingField?.mapping) {
      allMappings.push(mappingField.mapping);
    }
  } else {
    const mappingField = editor.fromTargetIndex(fieldPath);

    allMappings = mappingField.mappings;
  }

  // let field: FieldMapping | DictMapping;
  // let parentField: IIndexItem;
  // let mapping;
  const mappingField = isSource ? editor.fromSourceIndex(fieldPath) : editor.fromTargetIndex(fieldPath);

  // if (fieldPath.length > 1) {
  //   const fieldParent = [...fieldPath];
  //   fieldParent.pop();
  //   parentField = isSource ? editor.fromSourceIndex(fieldParent) : editor.fromTargetIndex(fieldParent);
  // } else {
  //   parentField = isSource ? editor.fromSourceIndex(fieldPath) : editor.fromTargetIndex(fieldPath);

  // }
  // if (parentField) {
  //   mapping = parentField.mapping //MappingLoader.load('root', parentField?.schema?.toJSON()) as DictMapping;
  // }
  allMappings.forEach(mapping => {
    const fieldProperties = (mapping as DictMapping).getProperties();
    const persistedConfig = mapping.config;
    errors.fieldError += countError(persistedConfig, fieldProperties);
    const processors = persistedConfig?.processors || [];
    processors?.forEach(processor => {
      try {
        const processorType = typeof processor === 'string' ? processor : processor.type;
        const processorProps = (mapping as DictMapping).getProcessorProperties(processorType);
        errors.processorError += countError(processor, processorProps);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('error in fieldErrorCounter ', mapping?.key, err, mapping?.toJSON());
      }
    });
  });

  return errors;
};

const countError = (persistedObject: {}, lookupDictionary: {}) => {
  let counter: number = 0;
  Object.keys(lookupDictionary).forEach(props => {
    if (!_.has(persistedObject, props) && lookupDictionary[props].required !== false) {
      counter += 1;
    }
    if (_.has(persistedObject, props)) {
      if (lookupDictionary[props].required !== false && _.isUndefined(persistedObject[props])) {
        counter += 1;
      }
      if (lookupDictionary[props].notEmpty && _.isEmpty(persistedObject[props])) {
        counter += 1;
      }
    }
  });

  return counter;
};

export const mappingSummary = (mappingSchema: ICollectionType | ISchema) => {
  const summary: { mappingEdges: Array<{ color: string; dangling: boolean }>; fieldsWithProcessor: number } = {
    mappingEdges: [],
    fieldsWithProcessor: 0
  };

  const traverse = (obj, path: string[]) => {
    Object.keys(obj)
      .filter(k => k !== '_meta')
      .forEach(k => {
        let p: string[] = [];

        if (_.has(obj[k], '_meta')) {
          // eslint-disable-next-line no-underscore-dangle
          p = [...path, obj[k]._meta.source];
          const color = obj[k]._meta.edgeProperties?.color ?? 'lightGrey';
          summary.mappingEdges.push({ color, dangling: !obj[k]._meta.edgeProperties?.target });
          if (obj[k]._meta?.processors?.length) {
            summary.fieldsWithProcessor += 1;
          }
          traverse(obj[k], p);
        } else {
          p = [...path, obj[k].source];
          const color = obj[k].edgeProperties?.color ?? 'lightGrey';
          summary.mappingEdges.push({ color, dangling: !obj[k].edgeProperties?.target });
          if (obj[k]?.processors?.length) {
            summary.fieldsWithProcessor += 1;
          }
        }
      });
  };

  traverse(mappingSchema, []);

  return summary;
};
